/**
 * useRecallTranscript
 * 
 * Connects to Recall.ai's built-in transcript WebSocket endpoint.
 * Available automatically when the page runs inside a Recall bot (output_media.camera.webpage).
 * No API keys needed — Recall provides transcripts natively to the webpage.
 * 
 * WS: wss://meeting-data.bot.recall.ai/api/v1/transcript
 */
import { useEffect, useRef, useState, useCallback } from 'react'

const RECALL_TRANSCRIPT_WS = 'wss://meeting-data.bot.recall.ai/api/v1/transcript'

export interface TranscriptChunk {
  speaker: string
  text: string
  startMs: number
  endMs: number
  isFinal: boolean
}

export function useRecallTranscript(
  enabled: boolean,
  onTranscript: (t: TranscriptChunk) => void
) {
  const wsRef = useRef<WebSocket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const onTranscriptRef = useRef(onTranscript)
  onTranscriptRef.current = onTranscript

  const connect = useCallback(() => {
    if (wsRef.current) return

    console.log('[Recall] Connecting to transcript WS...')
    const ws = new WebSocket(RECALL_TRANSCRIPT_WS)
    wsRef.current = ws

    ws.onopen = () => {
      console.log('[Recall] Transcript WS connected')
      setIsConnected(true)
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        const transcript = data?.transcript
        if (!transcript) return

        const words = transcript.words || []
        if (words.length === 0) return

        const text = words.map((w: any) => w.text).join(' ').trim()
        if (!text) return

        const speaker = String(words[0]?.speaker_id ?? words[0]?.participant_id ?? 'Speaker 1')
        const startMs = Math.round((words[0]?.start_time ?? 0) * 1000)
        const endMs = Math.round((words[words.length - 1]?.end_time ?? 0) * 1000)
        const isFinal = transcript.is_final ?? true

        onTranscriptRef.current({ speaker, text, startMs, endMs, isFinal })
      } catch (e) {
        console.warn('[Recall] WS parse error:', e)
      }
    }

    ws.onerror = (e) => {
      console.error('[Recall] WS error:', e)
      setIsConnected(false)
    }

    ws.onclose = (ev) => {
      console.log('[Recall] WS closed:', ev.code, ev.reason)
      setIsConnected(false)
      wsRef.current = null
      // Reconnect after 2s if still enabled
      setTimeout(() => {
        if (enabled) connect()
      }, 2000)
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) return
    connect()
    return () => {
      if (wsRef.current) {
        wsRef.current.onclose = null
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [enabled, connect])

  return { isConnected }
}
